import { useContext } from 'react';
import type { ContextType } from 'react';
import type { RouteObject } from 'react-router';
import {
  Navigate,
  NavigationType,
  UNSAFE_DataRouterContext,
  UNSAFE_RouteContext,
  matchRoutes,
  useLocation,
  useRouteError,
} from 'react-router';

import { NotFoundContext } from '../NotFoundContext';
import { isNotFoundError } from '../isNotFoundError';

// Handle of a `*` route that stands in for pages outside the route tree (test
// routers), so it counts as a page and not as a not-found catch-all.
export const standInForPagesHandle = { standInForPages: true };

const isPage = ({ children, path, handle }: RouteObject) =>
  !children?.length &&
  (!path?.endsWith('*') || handle === standInForPagesHandle);

// Nearest ancestor of the current URL that matches a page, outside the route
// rendering this component.
const useNearestRoutePath = () => {
  const { pathname } = useLocation();
  const routes = useContext(UNSAFE_DataRouterContext)?.router.routes ?? [];
  const currentRouteId =
    useContext(UNSAFE_RouteContext).matches.at(-1)?.route.id;

  const segments = pathname.split('/').filter(Boolean);
  for (let length = segments.length - 1; length >= 0; length -= 1) {
    const path = `/${segments.slice(0, length).join('/')}`;
    const matches = matchRoutes(routes, path);
    const route = matches?.at(-1)?.route;
    if (
      route &&
      isPage(route) &&
      matches.every(match => match.route.id !== currentRouteId)
    ) {
      return path;
    }
  }
  return null;
};

const RedirectTo = ({ pathname }: { pathname: string }) => {
  const { search, hash } = useLocation();

  return <Navigate to={{ pathname, search, hash }} replace />;
};

export const RedirectToParent = () => {
  const notFound = useContext(NotFoundContext);
  const target = useNearestRoutePath();

  return target === null ? notFound : <RedirectTo pathname={target} />;
};

type DataRouter = NonNullable<
  ContextType<typeof UNSAFE_DataRouterContext>
>['router'];

// Targets of each router's current NOT_FOUND redirect chain: a run of
// `replace` navigations. A parent page that redirects back to the failing child
// (e.g. `$fixture` → `lineups`) sends us to the same target from a new location.
// `location.state` would not survive that page's own `<Navigate>`.
const redirectChains = new WeakMap<
  DataRouter,
  { key: string; target: string }[]
>();

const getRedirectChain = (router: DataRouter) => {
  let chain = redirectChains.get(router);
  if (!chain) {
    chain = [];
    redirectChains.set(router, chain);
    let { location } = router.state;
    router.subscribe(state => {
      if (state.location === location) {
        return;
      }
      location = state.location;
      if (state.historyAction !== NavigationType.Replace) {
        redirectChains.set(router, []);
      }
    });
  }
  return chain;
};

const RedirectToNearestRoute = () => {
  const { key } = useLocation();
  const router = useContext(UNSAFE_DataRouterContext)?.router;
  const notFound = useContext(NotFoundContext);
  const target = useNearestRoutePath();

  if (!router || target === null) {
    return notFound;
  }

  const chain = getRedirectChain(router);
  if (chain.some(hop => hop.target === target && hop.key !== key)) {
    return notFound;
  }
  chain.push({ key, target });

  return <RedirectTo pathname={target} />;
};

export const RedirectToParentOnNotFound = () => {
  const routeError = useRouteError();
  if (isNotFoundError(routeError)) {
    return <RedirectToNearestRoute />;
  }
  throw routeError;
};
